/* eslint-disable complexity */
import { useMemo, useRef, useState } from 'react';
import { Center, Spinner } from 'src/components/atoms';
import {
  BarHorizontal,
  Donut,
  HeroMetric,
  Legends,
  LineChart,
  Table,
} from 'src/components/molecules';
import {
  GlobalFilter,
  LocalFilter,
  PaginatedTable,
  SelectedLocalFilters,
} from 'src/components/organisms';
import { GraphPanel } from 'src/components/organisms/GraphPanel';
import { useElementDimensions } from 'src/hooks';
import { useOrganisationInsightsData } from 'src/hooks/insights/useOrganisationInsightsData';
import { InsightsDashboard } from 'src/types';
import { CHART_IDS } from 'src/utils/analytics';
import {
  ClickableTitle,
  GraphContainer,
  GraphTableContainer,
  PageTitle,
} from '../EthicsOperationsDataview/EthicsOperationsDataview.styles';
import { getColumnNames } from '../EthicsOperationsDataview/helpers';
import { useInsightsGlobalFilters } from '../EthicsOperationsDataview/hooks';
import {
  BarChartContainer,
  DonutAndBarHorizontalContainer,
  DonutContainer,
  EthicalHealthDataviewStyled,
  HeroMetricContainer,
  InsightsDisclaimer,
} from './EthicalHealthDataview.styles';
import {
  useGetLineGraphData,
  useHeroMetrics,
  useIssueCategoriesByDepartment,
  useOrganisationalHealthDownloads,
} from './hooks';
import { useFormatPdfFilter } from './hooks/useFormatPdfFilter';

export const EthicalHealthDataview = () => {
  const [localFiltersOpen, setLocalFiltersOpen] = useState(false);
  const defaultLocalFilter: SelectedLocalFilters = {
    options: {},
    saveFunction: () => {},
  };

  const [selectedLocalFilters, setSelectedLocalFilters] =
    useState<SelectedLocalFilters>(defaultLocalFilter);
  const [filteredChartId, setFilteredChartId] = useState<
    CHART_IDS | undefined
  >();

  const donutRef = useRef<HTMLDivElement | null>(null);
  const lineChartRef = useRef<HTMLDivElement | null>(null);

  const {
    organisationSites,
    organisationCountries,
    organisationCaseCountries,
    organisationCaseRegions,
    organisationDepartments,
    organisationCategories,
    oldestReportSubmittedDate,
  } = useOrganisationInsightsData();

  const {
    globalFilterOpen,
    closeGlobalFilter,
    globalFilterProps,
    locationTitle,
    onLocationTitleClick,
    selectedSites: globalFilterSites,
    selectedCountries: globalFilterCountries,
    selectedCaseCountryCodes,
    selectedCaseRegionIds,
    timeframeTitle,
    selectedTimeframe: globalFilterTimeframe,
    onTimeTitleClick,
    titleHoverDisplay,
  } = useInsightsGlobalFilters({
    organisationSites,
    organisationCountries,
    organisationCaseCountries,
    organisationCaseRegions,
    oldestReportSubmittedDate,
    dashboard: InsightsDashboard.ORGANISATIONAL,
  });

  const { data: heroMetricData, isLoading } = useHeroMetrics({
    globalFilterSites,
    globalFilterCountries,
    selectedCaseCountryCodes,
    selectedCaseRegionIds,
    globalFilterTimeframe,
  });

  const {
    lineChartLegend,
    xTitle: lineChartXTitle,
    yTitle: lineChartYTitle,
    lineData,
    timeframe: lineChartTimeframe,
    onLineChartLegendClick,
    localFilterOptions: lineChartLocalFilters,
    saveLocalFilters: saveLineChartLocalFilters,
    dataIsLoading: lineChartDataLoading,
    categoriesOverTime,
    dateGroup,
    setDateGroup,
    tableColumns: lineChartTableColumns,
    tableData: lineChartTableData,
    handleColumnSort: handleLineChartColumnSort,
    sortedColumn: lineChartSortedColumn,
    xAxisLabelCount: lineChartAxisLabelCount,
    pdfIsDownloading: lineChartPdfIsDownloading,
    setPdfIsDownloading: setLineChartPdfIsDownloading,
    localLocationFilter: lineChartLocalLocationFilter,
    resetLocalFilters: resetLineChartLocalFilterOptions,
  } = useGetLineGraphData({
    globalFilterSites,
    globalFilterCountries,
    selectedCaseCountryCodes,
    selectedCaseRegionIds,
    globalFilterTimeframe,
    organisationData: {
      sites: organisationSites,
      countries: organisationCountries,
      departments: organisationDepartments,
      oldestReportSubmittedAtDate: oldestReportSubmittedDate,
      categories: organisationCategories,
    },
  });
  const {
    donutData,
    onDonutClick,
    donutLegend,
    centerLabel,
    barData,
    xTitle: barXTitle,
    yTitle: barYTitle,
    barColor,
    xAxisMax,
    localFilterOptions: donutChartLocalFilters,
    saveLocalFilters: saveDonutChartLocalFilters,
    dataIsLoading: donutDataLoading,
    resetLocalFilters: resetDonutLocalFilterOptions,
    categoriesByDepartment,
    onLegendClick: onDonutLegendClick,
    selectedSegment: selectedDonutSegment,
    tableColumns: donutTableColumns,
    tableData: donutTableData,
    handleColumnSort: handleDonutColumnSort,
    sortedColumn: donutSortedColumn,
    categoryAxisLabelCount: donutAxisLabelCount,
    pdfIsDownloading: donutPdfIsDownloading,
    setPdfIsDownloading: setDonutPdfIsDownloading,
    localLocationFilter: donutLocalLocationFilter,
  } = useIssueCategoriesByDepartment({
    globalFilterSites,
    globalFilterCountries,
    selectedCaseCountryCodes,
    selectedCaseRegionIds,
    globalFilterTimeframe,
    organisationData: {
      sites: organisationSites,
      countries: organisationCountries,
      departments: organisationDepartments,
      oldestReportSubmittedAtDate: oldestReportSubmittedDate,
      categories: organisationCategories,
    },
  });

  const { downloadCategoriesOverTime, downloadCategoriesByDepartment } =
    useOrganisationalHealthDownloads(
      categoriesOverTime,
      categoriesByDepartment,
    );

  const { width: lineChartWidth, height: lineChartHeight } =
    useElementDimensions(lineChartRef);
  const { width: donutWidth, height: donutHeight } =
    useElementDimensions(donutRef);
  const lineChartFilterData = useFormatPdfFilter(lineChartLocalFilters);
  const donutChartFilterData = useFormatPdfFilter(donutChartLocalFilters);

  const modifiedBarData = useMemo(() => {
    const counts: Record<string, number> = {};

    return barData?.map((d) => {
      if (!counts[d.label]) {
        counts[d.label] = 0;
      }

      counts[d.label]++;

      const newLabel =
        counts[d.label] === 1 ? d.label : `${d.label} (${counts[d.label]})`;

      return {
        ...d,
        label: newLabel,
      };
    });
  }, [barData]);

  const columnsMemo = useMemo(
    () => [{ label: 'Filter' }, { label: 'Selected' }],
    [],
  );
  const visibleColumnsMemo = useMemo(
    () => [{ label: 'Filter' }, { label: 'Selected' }],
    [],
  );

  if (isLoading) {
    return (
      <Center>
        <Spinner variant="secondary" size="xlarge" />
      </Center>
    );
  }

  const renderDonutData = () => {
    if (donutDataLoading) {
      return (
        <Center style={{ minHeight: '518px' }}>
          <Spinner variant="secondary" size="xlarge" />
        </Center>
      );
    } else {
      return (
        <DonutAndBarHorizontalContainer>
          <DonutContainer>
            <Donut
              data={donutData}
              onClick={onDonutClick}
              centerLabel={centerLabel}
              selectedSegment={selectedDonutSegment}
            />
          </DonutContainer>
          {modifiedBarData && (
            <BarChartContainer>
              <BarHorizontal
                xTitle={barXTitle}
                yTitle={barYTitle}
                data={modifiedBarData}
                monocolor={barColor}
                xAxisMax={xAxisMax}
                graphId="issue-categories-horizontal-bar"
                tooltip={true}
                tooltipType={'donut-and-bar-horizontal'}
                pdfIsDownloading={donutPdfIsDownloading}
                pdfDownloadHeight={Math.max(donutAxisLabelCount * 40, 450)}
              />
            </BarChartContainer>
          )}
        </DonutAndBarHorizontalContainer>
      );
    }
  };

  const dashboardContent = (
    <>
      <HeroMetricContainer>
        {heroMetricData.map((metric) => (
          <HeroMetric
            data-testid={metric.testId}
            key={metric.testId}
            title={metric.title}
            descriptor={metric.descriptor}
            tooltipText={metric.tooltip}
            metrics={metric.metrics}
            dashboard="organisational"
            metric_id={metric.id}
          />
        ))}
      </HeroMetricContainer>
      <GraphPanel
        chartTitle={'Issue categories by department'}
        chartTitleDescription="Total number of issues per department, subsequently broken down into their respective issue categories. The chart shows both active and closed cases."
        headers={['Chart', 'Table']}
        dashboard="organisational"
        chartId={CHART_IDS.categories_per_department}
        pdfDownloadRef={donutRef}
        pdfDownloadWidth={Math.max(1000, donutWidth)}
        pdfDownloadHeight={Math.max(
          200 + donutAxisLabelCount * 40,
          donutHeight,
        )}
        togglePdfDownloading={setDonutPdfIsDownloading}
        panels={[
          <div
            ref={donutRef}
            key={`${CHART_IDS.categories_per_department}-graph`}
          >
            {renderDonutData()}
            <Legends
              items={donutLegend}
              variant={'dot'}
              onClick={onDonutLegendClick}
              alwaysShowOverflow={donutPdfIsDownloading}
            />
            {donutPdfIsDownloading && (
              <Table
                columns={columnsMemo}
                visibleColumns={visibleColumnsMemo}
                tableData={
                  donutChartFilterData as {
                    cellData: (string | number)[];
                    row: string;
                  }[]
                }
                emptyText=""
              />
            )}
          </div>,
          <GraphTableContainer
            key={`${CHART_IDS.categories_per_department}-table`}
          >
            <PaginatedTable
              columns={donutTableColumns}
              visibleColumns={donutTableColumns}
              sortableColumns={getColumnNames(donutTableColumns)}
              tableData={donutTableData}
              sortedColumn={donutSortedColumn}
              onColumnSort={handleDonutColumnSort}
              maxRows={7}
              fixedRowHeight
              isFullWidth
              emptyText=""
            />
          </GraphTableContainer>,
        ]}
        onFiltersOpen={() => {
          setLocalFiltersOpen(true);
          setSelectedLocalFilters({
            options: donutChartLocalFilters,
            saveFunction: saveDonutChartLocalFilters,
            resetOptions: resetDonutLocalFilterOptions,
            locationFilter: donutLocalLocationFilter,
            originalSelectedLocationFilter: donutLocalLocationFilter,
          });
          setFilteredChartId(CHART_IDS.categories_per_department);
        }}
        onDownloadInsights={({ label }, print) =>
          downloadCategoriesByDepartment(label, donutChartLocalFilters, print)
        }
      />
      {lineData && (
        <GraphPanel
          chartTitle="Reported issue categories over time"
          chartTitleDescription="Total volume of issue categories over time. This chart shows issue categories from both active and closed cases."
          headers={['Chart', 'Table']}
          dashboard="organisational"
          chartId={CHART_IDS.categories_over_time}
          buttonGroupOptions={['Month', 'Quarter']}
          selectedButtonGroupOption={dateGroup}
          setSelectedButtonGroupOption={
            setDateGroup as (option: string) => void
          }
          pdfDownloadRef={lineChartRef}
          pdfDownloadWidth={Math.max(
            lineChartWidth,
            150 + lineChartAxisLabelCount * 100,
          )}
          pdfDownloadHeight={lineChartHeight}
          togglePdfDownloading={setLineChartPdfIsDownloading}
          panels={[
            <div
              ref={lineChartRef}
              key={`${CHART_IDS.categories_over_time}-graph`}
            >
              {lineChartDataLoading ? (
                <Center style={{ minHeight: '518px' }}>
                  <Spinner variant="secondary" size="xlarge" />
                </Center>
              ) : (
                <GraphContainer>
                  <LineChart
                    xTitle={lineChartXTitle ?? 'Time (month)'}
                    yTitle={lineChartYTitle ?? 'Number of reports'}
                    data={lineData ?? []}
                    timeframe={lineChartTimeframe}
                    dateGroup={dateGroup}
                    pdfIsDownloading={lineChartPdfIsDownloading}
                    pdfDownloadWidth={Math.max(
                      50 + lineChartAxisLabelCount * 100,
                      lineChartWidth,
                    )}
                  />
                </GraphContainer>
              )}
              <Legends
                items={lineChartLegend}
                variant="dot+line"
                onClick={onLineChartLegendClick}
                alwaysShowOverflow={lineChartPdfIsDownloading}
              />
              {lineChartPdfIsDownloading && (
                <Table
                  columns={columnsMemo}
                  visibleColumns={visibleColumnsMemo}
                  tableData={
                    lineChartFilterData as {
                      cellData: (string | number)[];
                      row: string;
                    }[]
                  }
                  emptyText=""
                />
              )}
            </div>,
            <GraphTableContainer
              key={`${CHART_IDS.categories_over_time}-table`}
            >
              <PaginatedTable
                columns={lineChartTableColumns}
                visibleColumns={lineChartTableColumns}
                sortableColumns={[
                  lineChartTableColumns[2].label,
                  lineChartTableColumns[3].label,
                ]}
                tableData={lineChartTableData}
                sortedColumn={lineChartSortedColumn}
                maxRows={7}
                allowRowSelect={false}
                onColumnSort={handleLineChartColumnSort}
                isFullWidth
                emptyText=""
              />
            </GraphTableContainer>,
          ]}
          onFiltersOpen={() => {
            setLocalFiltersOpen(true);
            setSelectedLocalFilters({
              options: lineChartLocalFilters,
              saveFunction: saveLineChartLocalFilters,
              resetOptions: resetLineChartLocalFilterOptions,
              locationFilter: lineChartLocalLocationFilter,
              originalSelectedLocationFilter: lineChartLocalLocationFilter,
            });
            setFilteredChartId(CHART_IDS.categories_over_time);
          }}
          onDownloadInsights={({ label }, print) =>
            downloadCategoriesOverTime(label, lineChartLocalFilters, print)
          }
        />
      )}
      {globalFilterOpen && (
        <GlobalFilter
          {...globalFilterProps}
          closeFilters={closeGlobalFilter}
          isOpen={globalFilterOpen}
          dashboard={InsightsDashboard.ORGANISATIONAL}
        />
      )}
      <LocalFilter
        isOpen={localFiltersOpen}
        filters={selectedLocalFilters.options}
        closeFilters={() => {
          setLocalFiltersOpen(false);
          setSelectedLocalFilters(defaultLocalFilter);
          setFilteredChartId(undefined);
        }}
        resetFilters={selectedLocalFilters.resetOptions}
        saveFilters={(filters) => {
          selectedLocalFilters.saveFunction(filters);
          setLocalFiltersOpen(false);
        }}
        dashboard={InsightsDashboard.ORGANISATIONAL}
        chartId={filteredChartId}
        selectedLocationFilter={selectedLocalFilters.locationFilter}
        originalSelectedLocationFilter={
          selectedLocalFilters?.originalSelectedLocationFilter
        }
        setSelectedLocationFilter={(locationFilter) =>
          setSelectedLocalFilters({ ...selectedLocalFilters, locationFilter })
        }
      />
      <InsightsDisclaimer>
        <p>
          * All data in Integrity Intelligence is related to the named offender
          in the report
        </p>
      </InsightsDisclaimer>
    </>
  );

  return (
    <EthicalHealthDataviewStyled data-testid="organisational-dashboard-container">
      <PageTitle>
        Ethical Health in{' '}
        <ClickableTitle
          onClick={onLocationTitleClick}
          title={titleHoverDisplay}
        >
          {locationTitle}
        </ClickableTitle>{' '}
        for{' '}
        <ClickableTitle
          onClick={onTimeTitleClick}
          data-testid={'clickable-title-timeframe'}
        >
          {timeframeTitle}
        </ClickableTitle>
      </PageTitle>
      {dashboardContent}
    </EthicalHealthDataviewStyled>
  );
};
